import { PRODUCT_SLUG } from '@/shared/constants/app/routes.constant.ts';

export const FAQ_CATEGORY = {
  PRODUCT: 'PRODUCT',
  BILLING: 'BILLING',
  ACCOUNT: 'ACCOUNT',
  TECH: 'TECH',
  ETC: 'ETC',
} as const;

export const FAQ_CATEGORIES = [
  FAQ_CATEGORY.PRODUCT,
  FAQ_CATEGORY.BILLING,
  FAQ_CATEGORY.ACCOUNT,
  FAQ_CATEGORY.TECH,
  FAQ_CATEGORY.ETC,
] as const;

export type FaqCategory = (typeof FAQ_CATEGORIES)[number];

export interface FaqOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export const FAQ_ALL_OPTION = { value: null, label: '전체' } as const;

export const FAQ_CATEGORY_LABEL = {
  [FAQ_CATEGORY.PRODUCT]: '제품/기능',
  [FAQ_CATEGORY.BILLING]: '구매/결제',
  [FAQ_CATEGORY.ACCOUNT]: '계정',
  [FAQ_CATEGORY.TECH]: '설치/오류',
  [FAQ_CATEGORY.ETC]: '기타',
} as const satisfies Record<FaqCategory, string>;

export const FAQ_CATEGORY_OPTIONS: readonly FaqOption<FaqCategory>[] =
  FAQ_CATEGORIES.map((value) => ({
    value,
    label: FAQ_CATEGORY_LABEL[value],
  }));

export const FAQ_SUB_CATEGORY = {
  [FAQ_CATEGORY.PRODUCT]: {
    XCOST_PRO: PRODUCT_SLUG.XCOST_PRO,
    XPIXELS_AQC: PRODUCT_SLUG.XPIXELS_AQC,
    XPIXELS_SQC: PRODUCT_SLUG.XPIXELS_SQC,
    XPIXELS_EQC: PRODUCT_SLUG.XPIXELS_EQC,
    XPIXELS_MQC: PRODUCT_SLUG.XPIXELS_MQC,
    WORKNOTE: PRODUCT_SLUG.WORKNOTE,
  },
  [FAQ_CATEGORY.BILLING]: {
    PURCHASE: 'purchase',
    PAYMENT: 'payment',
    REFUND: 'refund',
    LICENSE: 'license',
  },
  [FAQ_CATEGORY.ACCOUNT]: {
    SIGNUP: 'signup',
    LOGIN: 'login',
    ORGANIZATION_MEMBER: 'organization-member',
    PERMISSION: 'permission',
  },
  [FAQ_CATEGORY.TECH]: {
    INSTALL: 'install',
    ERROR: 'error',
    UPDATE: 'update',
    COMPATIBILITY: 'compatibility',
  },
  [FAQ_CATEGORY.ETC]: {
    GENERAL: 'general',
  },
} as const;

/** DB/API의 varchar(50) 계약과 동일한 프론트 입력 상한. */
export const FAQ_SUB_CATEGORY_MAX_LENGTH = 50;

/** FAQ 작성 폼과 공개 화면이 함께 사용하는 카테고리별 중분류. */
export const FAQ_SUB_CATEGORY_OPTIONS = {
  [FAQ_CATEGORY.PRODUCT]: [
    { value: FAQ_SUB_CATEGORY.PRODUCT.XCOST_PRO, label: 'XCOST PRO' },
    { value: FAQ_SUB_CATEGORY.PRODUCT.XPIXELS_AQC, label: 'XPIXELS AQC' },
    { value: FAQ_SUB_CATEGORY.PRODUCT.XPIXELS_SQC, label: 'XPIXELS SQC' },
    { value: FAQ_SUB_CATEGORY.PRODUCT.XPIXELS_EQC, label: 'XPIXELS EQC' },
    { value: FAQ_SUB_CATEGORY.PRODUCT.XPIXELS_MQC, label: 'XPIXELS MQC' },
    { value: FAQ_SUB_CATEGORY.PRODUCT.WORKNOTE, label: 'WORKNOTE' },
  ],
  [FAQ_CATEGORY.BILLING]: [
    { value: FAQ_SUB_CATEGORY.BILLING.PURCHASE, label: '구매' },
    { value: FAQ_SUB_CATEGORY.BILLING.PAYMENT, label: '결제' },
    { value: FAQ_SUB_CATEGORY.BILLING.REFUND, label: '환불' },
    { value: FAQ_SUB_CATEGORY.BILLING.LICENSE, label: '라이선스' },
  ],
  [FAQ_CATEGORY.ACCOUNT]: [
    { value: FAQ_SUB_CATEGORY.ACCOUNT.SIGNUP, label: '회원가입' },
    { value: FAQ_SUB_CATEGORY.ACCOUNT.LOGIN, label: '로그인' },
    {
      value: FAQ_SUB_CATEGORY.ACCOUNT.ORGANIZATION_MEMBER,
      label: '조직/멤버',
    },
    { value: FAQ_SUB_CATEGORY.ACCOUNT.PERMISSION, label: '권한' },
  ],
  [FAQ_CATEGORY.TECH]: [
    { value: FAQ_SUB_CATEGORY.TECH.INSTALL, label: '설치' },
    { value: FAQ_SUB_CATEGORY.TECH.ERROR, label: '오류' },
    { value: FAQ_SUB_CATEGORY.TECH.UPDATE, label: '업데이트' },
    { value: FAQ_SUB_CATEGORY.TECH.COMPATIBILITY, label: '호환성' },
  ],
  [FAQ_CATEGORY.ETC]: [{ value: FAQ_SUB_CATEGORY.ETC.GENERAL, label: '일반' }],
} as const satisfies Record<FaqCategory, readonly FaqOption[]>;

type FaqSubCategoryOption =
  (typeof FAQ_SUB_CATEGORY_OPTIONS)[FaqCategory][number];
export type FaqSubCategory = FaqSubCategoryOption['value'];

export function isFaqCategory(value: unknown): value is FaqCategory {
  return (
    typeof value === 'string' &&
    (FAQ_CATEGORIES as readonly string[]).includes(value)
  );
}

export function getFaqCategoryLabel(category: unknown): string | null {
  return isFaqCategory(category) ? FAQ_CATEGORY_LABEL[category] : null;
}

export function getFaqSubCategoryOptions(
  category: unknown,
): readonly FaqOption[] {
  return isFaqCategory(category) ? FAQ_SUB_CATEGORY_OPTIONS[category] : [];
}

export function isFaqSubCategory(
  category: unknown,
  subCategory: unknown,
): subCategory is FaqSubCategory {
  return (
    typeof subCategory === 'string' &&
    getFaqSubCategoryOptions(category).some(
      (option) => option.value === subCategory,
    )
  );
}

export function getFaqSubCategoryLabel(
  category: unknown,
  subCategory: unknown,
): string | null {
  if (typeof subCategory !== 'string') return null;
  return (
    getFaqSubCategoryOptions(category).find(
      (option) => option.value === subCategory,
    )?.label ?? null
  );
}
